/**
 * Living drawings — the `creatures` store slice (own persisted zustand store, so it never fights the
 * main store) + the photo → creature pipeline.
 *
 * Public API (map / creature book / collections use these):
 *   useCreatures(worldId?)            creatures of the active profile, newest first (memoised)
 *   useCreature(id)                   one creature (or undefined)
 *   listCreatures({ profileId, worldId })   non-hook read
 *   creaturesForWorld(worldId, profileId?)  non-hook read for one world
 *   createCreature(blob, ctx)         cut a drawing photo out, save the PNG (IndexedDB) + metadata
 *   creatureImageUrl(meta)            object URL of the transparent PNG
 *
 * Images live in IndexedDB (lib/idb, key `creature-<id>`), metadata in `sketchling-creatures-v1`.
 * Everything works offline.
 */
import { useMemo } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { creatureName, creaturesFor, cutOutCreature, hash, MAX_CREATURES_PER_PROFILE, type CreatureMeta, type CreatureSource, type PathId } from '@sketchling/core';
import { appNow, uid, useApp } from '../../state/store';
import { blobToImg } from '../../lib/camera';
import { deleteImage, imageUrl, putImage } from '../../lib/idb';
import { WORLD_ART } from '../../art/worlds';

export type { CreatureMeta };

/* ----------------------------------- store ----------------------------------- */

export interface CreaturesState {
  creatures: CreatureMeta[];
  /** Ids the child deleted on this device (tombstones for the cloud sync, newest last). */
  removed?: string[];
  /** Replace one profile's creatures with the merged list from the cloud sync. */
  replaceProfileCreatures(profileId: string, list: CreatureMeta[]): void;
  addCreature(c: CreatureMeta): void;
  updateCreature(id: string, patch: Partial<CreatureMeta>): void;
  removeCreature(id: string): void;
}

/** Stable empty list for selectors. */
export const EMPTY_CREATURES: CreatureMeta[] = [];

const storage: StateStorage = Capacitor.isNativePlatform()
  ? {
      getItem: async (k) => (await Preferences.get({ key: k })).value,
      setItem: (k, v) => Preferences.set({ key: k, value: v }),
      removeItem: (k) => Preferences.remove({ key: k }),
    }
  : {
      getItem: (k) => {
        try {
          return localStorage.getItem(k);
        } catch {
          return null;
        }
      },
      setItem: (k, v) => {
        try {
          localStorage.setItem(k, v);
        } catch {
          /* quota */
        }
      },
      removeItem: (k) => {
        try {
          localStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      },
    };

export const CREATURES_STORAGE_KEY = 'sketchling-creatures-v1';

export const useCreatureStore = create<CreaturesState>()(
  persist(
    (set) => ({
      creatures: EMPTY_CREATURES,
      addCreature: (c) =>
        set((st) => {
          const mine = st.creatures.filter((x) => x.profileId === c.profileId);
          let list = [c, ...st.creatures.filter((x) => x.id !== c.id)];
          if (mine.length >= MAX_CREATURES_PER_PROFILE) {
            const oldest = creaturesFor(mine, c.profileId).slice(MAX_CREATURES_PER_PROFILE - 1);
            const drop = new Set(oldest.map((x) => x.id));
            for (const o of oldest) void deleteImage(o.imageKey);
            list = list.filter((x) => !drop.has(x.id));
          }
          return { creatures: list };
        }),
      updateCreature: (id, patch) => set((st) => ({ creatures: st.creatures.map((c) => (c.id === id ? { ...c, ...patch } : c)) })),
      removeCreature: (id) =>
        set((st) => {
          const c = st.creatures.find((x) => x.id === id);
          if (c) void deleteImage(c.imageKey);
          return { creatures: st.creatures.filter((x) => x.id !== id), removed: [...(st.removed ?? []).filter((x) => x !== id), id].slice(-500) };
        }),
      replaceProfileCreatures: (profileId, list) =>
        set((st) => {
          const keep = new Set(list.map((c) => c.id));
          for (const c of st.creatures) if (c.profileId === profileId && !keep.has(c.id)) void deleteImage(c.imageKey);
          return { creatures: [...list, ...st.creatures.filter((c) => c.profileId !== profileId)] };
        }),
    }),
    { name: CREATURES_STORAGE_KEY, version: 1, storage: createJSONStorage(() => storage) },
  ),
);

// Profiles removed (or the device reset) → drop their creatures.
useApp.subscribe((s, prev) => {
  if (s.profiles === prev.profiles) return;
  if (!useApp.persist.hasHydrated() || !useCreatureStore.persist.hasHydrated()) return;
  const ids = new Set(s.profiles.map((p) => p.id));
  const gone = useCreatureStore.getState().creatures.filter((c) => !ids.has(c.profileId));
  for (const c of gone) useCreatureStore.getState().removeCreature(c.id);
});

/* ----------------------------------- reads ----------------------------------- */

/** Creatures of the active profile (newest first), optionally only one world's. */
export function useCreatures(worldId?: PathId): CreatureMeta[] {
  const all = useCreatureStore((s) => s.creatures);
  const profileId = useApp((s) => s.activeProfileId);
  return useMemo(() => creaturesFor(all, profileId, worldId), [all, profileId, worldId]);
}

export function useCreature(id: string | null | undefined): CreatureMeta | undefined {
  return useCreatureStore((s) => (id ? s.creatures.find((c) => c.id === id) : undefined));
}

export function listCreatures(opts: { profileId?: string | null; worldId?: PathId } = {}): CreatureMeta[] {
  const profileId = opts.profileId === undefined ? useApp.getState().activeProfileId : opts.profileId;
  return creaturesFor(useCreatureStore.getState().creatures, profileId, opts.worldId);
}

/** Creatures living in one world (map): active profile unless `profileId` is given. */
export const creaturesForWorld = (worldId: PathId, profileId?: string | null) => listCreatures({ profileId, worldId });

export const creatureImageUrl = (c: Pick<CreatureMeta, 'imageKey'>) => imageUrl(c.imageKey);

/* ---------------------------------- create ---------------------------------- */

export interface CreateCreatureContext {
  profileId: string;
  worldId: PathId;
  subject: string;
  source: CreatureSource;
  drawingId?: string;
  journalId?: string;
}

const pending = new WeakMap<Blob, Promise<CreatureMeta | null>>();

function toPng(c: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('png'))), 'image/png'));
}

/** Pale tint for pencil-only drawings: one of the world's accents (deterministic per creature). */
function worldTint(worldId: PathId, id: string): string {
  const acc = WORLD_ART[worldId]?.theme.accents ?? ['#F2C14E', '#EE8C8C', '#7CC3CF'];
  return acc[hash(id) % acc.length]!;
}

/**
 * Cut the drawing out of its photo and keep it as a creature. Idempotent per blob (StrictMode,
 * re-renders). Resolves null when the photo has no drawing.
 */
export function createCreature(blob: Blob, ctx: CreateCreatureContext): Promise<CreatureMeta | null> {
  const known = pending.get(blob);
  if (known) return known;
  const p = (async () => {
    // let the reveal animation start before the pixel work
    await new Promise((r) => setTimeout(r, 30));
    const id = uid();
    const { img } = await blobToImg(blob, 900);
    const tint = worldTint(ctx.worldId, id);
    const cut = cutOutCreature(img, { analysisSide: 300, outSide: 520, tint });
    if (!cut) return null;
    const c = document.createElement('canvas');
    c.width = cut.width;
    c.height = cut.height;
    c.getContext('2d')!.putImageData(new ImageData(cut.rgba, cut.width, cut.height), 0, 0);
    const png = await toPng(c);
    const imageKey = `creature-${id}`;
    await putImage(imageKey, png);
    const meta: CreatureMeta = {
      id,
      profileId: ctx.profileId,
      name: creatureName(id),
      worldId: ctx.worldId,
      subject: ctx.subject,
      ...(ctx.drawingId ? { drawingId: ctx.drawingId } : {}),
      ...(ctx.journalId ? { journalId: ctx.journalId } : {}),
      source: ctx.source,
      createdAt: appNow().toISOString(),
      imageKey,
      width: cut.width,
      height: cut.height,
      eyes: cut.eyes,
      eyesDrawn: cut.eyesDrawn,
      kind: cut.kind,
      accent: cut.coloured ? cut.accent : tint,
      plays: 0,
    };
    useCreatureStore.getState().addCreature(meta);
    useApp.getState().logEvent('Creature Alive', { source: ctx.source, world: ctx.worldId, kind: cut.kind, eyesDrawn: cut.eyesDrawn });
    return meta;
  })().catch(() => null);
  pending.set(blob, p);
  return p;
}
